<template>
  <div class="ballot d-flex flex-column" :class="{ 'h-100': forceFullSizeSlide }">
    <header class="h1 text-center">{{ title }}</header>
    <div class="lead" v-html="description"></div>
    <template key="ballot-info" v-if="!votingRoundReport?.publishedAt">

      <template v-if="isHandraise && isObserver && isBallotOpen">
        <h3 class="fw-bold text-center fs-3">
          {{ currentlyVoting[0].title[firstAvailableLocale] }}
        </h3>
        <template v-if="showTurnoutElement">
          <hr>
          <BallotProgress :ballot="(ballot as BallotSlide)" class="mt-3 mb-4" />
        </template>
        <HandRaisePanelByName v-if="(ballot as BallotSlide).observerLayout === 'all_visible'" />
        <HandRaisePanelByVote v-else />
      </template>

      <template v-else>
        <template v-if="showTurnoutElement">
          <hr>
          <BallotProgress :ballot="(ballot as BallotSlide)" class="my-3" />
          <hr>
        </template>

        <div class="h5 mt-3">
          {{ currentlyVoting.length > 1 ? $t('js.ballot.ballots_in_slide') : $t('js.ballot.ballot_in_slide') }}
        </div>
        <div v-for="contest in currentlyVoting">
          <ContestHeader
            :contest="contest"
            :show-description="isBallotOpen"
            :show-options="true"
            :locale="firstAvailableLocale"
          />
          <AVBallot
            v-if="currentlyVoting.length === 1 && isBallotOpen"
            :contest="formatContest(contest)"
            :image-option='election.theme.imageOption'
            :observer-mode="true"
            :partial-results="handRaiseLiveResults"
            :selectionPile="{
              multiplier: 1,
              optionSelections: [],
              explicitBlank: false,
            }"
          />
          <template v-if="handRaiseLiveResults && isBallotOpen">
            <hr class="my-5">
            <HandRaiseLivePreview />
          </template>
        </div>
      </template>
    </template>

    <template v-if="(ballot as BallotSlide).state === 'finished'">
      <template v-if="!!votingRoundReport?.publishedAt">
        <template v-for="res in orchestrators">
          <template v-if="res.result">
            <Result
              :slide="ballot"
              :contest="contests.find((con) => con.reference === res.contestReference)"
              :theme-color="election.theme.color" theme="light" :result="res.result"
              :published="!!votingRoundReport?.publishedAt"
              :header="contests.find((con) => con.reference === res.contestReference).title[firstAvailableLocale]"
              :voting-round-report="votingRoundReport"
            />
          </template>
        </template>
      </template>
      <div v-else class="p-3 text-center">
        <p class="mb-0">{{ $t('js.ballot.not_published') }}</p>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { mapState } from "pinia";
import { defineComponent } from "vue";
import Result from "../shared/Result.vue";
import Progress from "@/components/shared/Progress.vue";
import Option from "../shared/Option.vue";
import Countdown from "../shared/Countdown.vue";
import BallotProgress from "../shared/BallotProgress.vue";
import ContestHeader from "@/components/shared/ContestHeader.vue";
import HandRaisePanelByVote from "./HandRaisePanelByVote.vue";
import HandRaisePanelByName from "./HandRaisePanelByName.vue";
import HandRaiseLivePreview from "../shared/HandRaiseLivePreview.vue";
import { formatContest, orderContests, orderResults } from "@/entrypoints/shared/contest_utilities";
import { useSharedStore } from "@/entrypoints/stores/shared";
import { usePresentationStore } from "@/entrypoints/stores/presentation";
import { useHandRaiseStore } from "@/entrypoints/stores/hand_raise";
import type {
  PropType,
  ConferenceContest,
  ConferenceVotingRound,
  E2E,
  LiveContestState,
  Slide,
  BallotSlide,
} from "@/types";

export default defineComponent({
  inject: ["stickyAlertsSubscribe"],
  components: {
    BallotProgress,
    Countdown,
    Option,
    Result,
    Progress,
    ContestHeader,
    HandRaisePanelByVote,
    HandRaisePanelByName,
    HandRaiseLivePreview,
  },
  props: {
    ballot: {
      type: Object as PropType<Slide>,
      default: null,
    },
  },
  data() {
    return {
      showErrors: false,
      showTurnout: true,
    };
  },
  computed: {
    ...mapState(useSharedStore, ["election", "firstAvailableLocale"]),
    ...mapState(usePresentationStore, ["contests", "votingRounds", "votingRoundReports", "forceFullSizeSlide", "activeHandRaiseOrchestrator"]),
    ...mapState(useHandRaiseStore, ["handRaiseLiveResults"]),
    includedContests() {
      return this.contests.filter((con: ConferenceContest) =>
        this.votingRound.contestReferences.includes(con.reference));
    },
    votingRound() {
      return this.votingRounds.find((vr: ConferenceVotingRound) =>
        vr.reference === (this.ballot as BallotSlide).votingRoundReference);
    },
    votingRoundReport() {
      return this.votingRoundReports?.find((rep: E2E.VotingRoundReport) =>
        rep.votingRoundReference === this.votingRound.reference);
    },
    orchestrators() {
      if (!this.votingRoundReport) return [];

      const filteredResults = this.votingRoundReport.contestResults.filter((res: E2E.ContestResult) =>
        this.votingRound.contestReferences.includes(res.contestReference));

      return this.orderResults(filteredResults, this.votingRound.contestPositions);
    },
    showTurnoutElement() {
      return this.showTurnout && this.isBallotOpen;
    },
    stickies() {
      const stickies = [];
      if (this.ballot) {
        stickies.push({ type: "ballotStatus", ballot: this.ballot });
      }
      return stickies;
    },
    description() {
      return this.ballot.description[this.firstAvailableLocale];
    },
    resultPublished() {
      return (this.ballot as BallotSlide).state === "finished" && !!this.votingRoundReport.publishedAt;
    },
    currentlyVoting() {
      const vrContests = this.contests.filter((contest: ConferenceContest) =>
        this.votingRound.contestReferences.includes(contest.reference));
      return this.orderContests(vrContests, this.votingRound.contestPositions);
    },
    title() {
      return this.ballot.title[this.firstAvailableLocale];
    },
    isHandraise() {
      return !!this.activeHandRaiseOrchestrator;
    },
    isBallotOpen() {
      return this.ballotStateIn(["open", "countdown"]);
    },
    isObserver() {
      return window.location.href.search(/observe\b/) >= 0;
    },
  },
  methods: {
    formatContest,
    orderContests,
    orderResults,
    ballotStateIn(ballotStates: Array<LiveContestState>) {
      if (this.ballot) {
        if (Array.isArray(ballotStates)) return ballotStates.includes((this.ballot as BallotSlide).state);
        else return ballotStates === (this.ballot as BallotSlide).state;
      }
      return false;
    },
  },
  mounted() {
    this.stickyAlertsSubscribe(this);
  },
});
</script>
